/**
 * The places a journey characteristic is scored on, narrowed to the route.
 *
 * "Scenic 1.00" picks between the ways round by counting parks near each one,
 * and a drawn line cannot show that it did: two routes through a city look
 * alike whatever the reason one was chosen. This is the evidence, so the
 * slider can be seen working.
 *
 * The categories and the corridor width both match the planner's own — see
 * characteristicCategories and roadRadiusM in internal/planning. Showing a
 * wider set than was counted would be a different claim than the score makes.
 */

/** Matches characteristicCategories in the planning service. */
export const CHARACTER_CATEGORIES: Record<string, string[]> = {
  scenic: ['park', 'garden', 'viewpoint', 'water_body', 'nature_reserve',
    'beach', 'waterfall', 'peak'],
  safety: ['hospital', 'police', 'fire_station'],
};

/**
 * Whether a characteristic is looking FOR these places or away from them.
 *
 * Everything left here seeks. Quiet was the only one that shunned, and it is
 * gone — from both forms, from the themes panel, and now from the emphases
 * themselves, which kept setting it long after the control disappeared.
 *
 * Kept as a map rather than folded away, because the distinction is real and
 * the next characteristic that wants to be drawn as something a route stayed
 * clear of will need it. Absent means seek, so a saved plan still carrying an
 * old avoid-sense key rings like anything else rather than not at all.
 */
export const CHARACTER_SENSE: Record<string, 'seek' | 'avoid'> = {
  scenic: 'seek',
  safety: 'seek',
};

/** The planner's default roadRadiusM: how near a place must be to count. */
export const CHARACTER_RADIUS_M = 400;

/** Metres per degree of latitude — near enough at any latitude we serve. */
const M_PER_DEG_LAT = 111_320;

/**
 * Distance in metres from a point to a segment, on an equirectangular
 * approximation.
 *
 * Exact enough at 400 m: the error over a few hundred metres is centimetres,
 * and the alternative is a projection library for a decision about whether to
 * draw a circle.
 */
function metresToSegment(
  pLat: number, pLng: number,
  aLat: number, aLng: number,
  bLat: number, bLng: number,
): number {
  const scale = Math.cos((pLat * Math.PI) / 180);
  const px = pLng * scale, py = pLat;
  const ax = aLng * scale, ay = aLat;
  const bx = bLng * scale, by = bLat;

  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  // A zero-length segment is a point; fall through to the distance to it.
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));

  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy) * M_PER_DEG_LAT;
}

interface Feature {
  geometry?: { type?: string; coordinates?: [number, number] };
  properties?: Record<string, unknown>;
}

/**
 * Keeps only the features within `withinM` of the line.
 *
 * Returns a FeatureCollection whatever happens, so a caller can hand the
 * result straight to the map without testing for emptiness.
 */
export function placesAlongRoute(
  collection: unknown,
  line: { lat: number; lng: number }[],
  /**
   * Which sense each category was asked about, so a kept place can say
   * whether the route wanted it or wanted away from it.
   */
  senseByCategory: Record<string, 'seek' | 'avoid'> = {},
  withinM = CHARACTER_RADIUS_M,
): { type: 'FeatureCollection'; features: unknown[] } {
  const empty = { type: 'FeatureCollection' as const, features: [] as unknown[] };
  const features = (collection as { features?: Feature[] } | null)?.features;
  if (!Array.isArray(features) || line.length < 2) return empty;

  const kept = features.filter((f) => {
    const c = f?.geometry?.coordinates;
    if (f?.geometry?.type !== 'Point' || !Array.isArray(c)) return false;

    const [lng, lat] = c;
    for (let i = 1; i < line.length; i += 1) {
      const d = metresToSegment(lat, lng, line[i - 1].lat, line[i - 1].lng, line[i].lat, line[i].lng);
      if (d <= withinM) return true;
    }

    return false;
  });

  /*
   * Tagged rather than split into two collections: one source, one layer, and
   * the paint decides — which keeps a place that belongs to two answers at
   * once from being drawn twice on top of itself.
   */
  const tagged = kept.map((f) => ({
    ...f,
    properties: {
      ...(f.properties ?? {}),
      characterSense: senseByCategory[String(f.properties?.categoryKey ?? '')] ?? 'seek',
    },
  }));

  return { type: 'FeatureCollection', features: tagged };
}
